import { BlogPost } from "./types";

export const reducirImagenParaEmail: BlogPost = {
  slug: "reducir-imagen-para-email",
  title: "Reducir imagen para email: guía rápida por cliente de correo",
  description: "Guía práctica para reducir imágenes según el cliente de correo que uses: Gmail, Outlook, Apple Mail y plataformas de email marketing. Límites exactos y proceso paso a paso.",
  date: "2026-03-24",
  readTime: "7 min",
  keywords: "reducir imagen email, comprimir foto correo, imagen adjunta email tamaño, gmail adjunto imagen, outlook imagen tamaño",
  content: `
## Límites de adjuntos por cliente de correo

Cada cliente de email tiene su propio límite de adjuntos, y lo que funciona en uno puede fallar en otro:

| Cliente de correo | Límite de adjuntos | Notas |
|---|---|---|
| Gmail | 25MB por email | Ofrece Drive automáticamente si superas el límite |
| Outlook.com / Hotmail | 20MB | Puede usar OneDrive para archivos grandes |
| Yahoo Mail | 25MB | Soporta adjuntos múltiples |
| Apple Mail / iCloud | 20MB | iCloud Mail Drop para archivos grandes |
| Correo corporativo (Exchange) | Varía: 5-20MB | El administrador puede configurarlo más bajo |
| Correo universitario | Varía: 5-15MB | Frecuentemente limitado |

**El problema real no es tu límite, sino el del servidor del destinatario.** Aunque Gmail acepta 25MB, el servidor del destinatario puede rechazar emails con adjuntos de más de 10MB.

La regla universal segura: mantén el total de adjuntos por debajo de 10MB, idealmente bajo 5MB.

## Reducción en Gmail

Gmail tiene una característica útil: cuando adjuntas imágenes desde el teléfono, a veces ofrece reducirlas automáticamente.

### Adjuntar imágenes en Gmail (web)

1. Haz clic en el ícono de clip (adjuntar archivo) en la barra inferior del editor
2. Selecciona tus imágenes
3. Gmail las adjunta sin compresión adicional — el peso del archivo es el peso original

**Importante:** si una imagen supera los 25MB, Gmail automáticamente la sube a Drive y adjunta un enlace.

### Adjuntar imágenes en Gmail (app móvil)

Al adjuntar desde la app de Gmail, la app puede ofrecer insertar imagen "inline" o como adjunto.
Para enviar en calidad original: adjunta como archivo (clip), no como imagen incrustada.

## Reducción en Outlook

### Outlook de escritorio (Windows)

Outlook 2016, 2019, 2021 y 365 tienen opciones de reducción de imágenes:

1. Compone el email y adjunta las imágenes
2. A veces aparece una barra amarilla que pregunta "¿Redimensionar imágenes?"
3. Elige el tamaño: "Documento grande (1024×768)", "Documento pequeño (640×480)" o "Móvil (320×240)"

Alternativa para imágenes insertadas en el cuerpo:
- **Insertar → Imagen** (inline)
- Haz clic derecho en la imagen → Formato de imagen → Comprimir imágenes
- Selecciona resolución de destino: Web (96 ppi) o Email (96 ppi)

### Outlook Web (OWA)

Outlook en web no tiene opciones de reducción automática. Debes comprimir las imágenes antes de adjuntarlas usando nuestro [compresor de imágenes](/).

## Reducción en Apple Mail e iPhone

### iPhone: compartir foto reducida desde Fotos

1. Abre la app Fotos y selecciona las fotos a enviar
2. Toca el botón de compartir (cuadrado con flecha)
3. Selecciona "Mail"
4. Al intentar enviar, aparece: **¿Reducir el tamaño de las imágenes?**
5. Opciones: "Pequeño" (320×240), "Mediano" (640×480), "Grande" (1920×...) o "Tamaño real"

Para la mayoría de envíos personales: selecciona "Grande". Para fotos de trabajo donde importa la calidad: "Tamaño real" y pre-optimiza tú la imagen con nuestro compresor.

### Mac: reducir con Vista Previa antes de adjuntar

1. Abre la imagen en Vista Previa (la app nativa de Mac)
2. Ve a **Herramientas → Ajustar tamaño**
3. Cambia el ancho a 1200px
4. Guarda la copia — no sobreescribas el original
5. Adjunta la imagen reducida al email

## Proceso recomendado: reducción manual antes de adjuntar

Para el máximo control de calidad y peso, optimiza tus imágenes antes de adjuntarlas:

### Para fotos casuales (viajes, eventos personales)

1. Sube al [compresor de imágenes](/)
2. Selecciona redimensionado a 1200px de ancho
3. Calidad JPEG: 80%
4. Resultado esperado: 150-280KB por foto
5. Total para 10 fotos: ~2MB — perfectamente adjuntable en cualquier cliente

### Para documentos o fotos importantes de trabajo

1. Sube al [compresor de imágenes](/)
2. Redimensiona a máximo 1600px de ancho
3. Calidad JPEG: 85% (algo más para preservar detalles finos)
4. Resultado: 250-450KB

### Para fotos de alta resolución (cámara profesional)

Si el destinatario necesita imprimir la foto, usa Google Drive o WeTransfer en lugar de adjuntar.
Si solo necesita verla en pantalla: redimensiona a 1600px, JPEG 85%, resultado ~400-700KB.

## Cuándo no adjuntar la imagen

Algunos casos requieren enviar la imagen de otra forma:

**Envía por enlace cuando:**
- La imagen es un archivo RAW o de alta resolución para impresión
- El álbum supera las 20 fotos
- El destinatario necesita descargar en calidad original
- Envías imágenes a múltiples destinatarios y no quieres saturar todas las bandejas

**Herramientas:** Google Drive (enlace de carpeta), Dropbox (enlace público), WeTransfer (gratis hasta 2GB).

**Adjunta directamente cuando:**
- Son 1-5 fotos para uso personal o trabajo normal
- El destinatario necesita guardar el archivo en su ordenador
- Es un documento fotográfico de trámite (factura, DNI, contrato)

## Imágenes en firmas de email: configuración correcta

La firma de email es un caso especial porque acompaña a cada mensaje que envías. Una firma mal optimizada puede hacer que tu email tarde más en enviarse, activar filtros de spam, o llenar la bandeja del destinatario más rápidamente.

**Para logos en la firma:**
- Máximo 200×60px para logos horizontales, 80×80px para logos cuadrados
- PNG con compresión optimizada
- Peso máximo: 15-20KB

**Para foto de perfil en la firma:**
- 80×80px o 100×100px
- JPEG 85%
- Peso máximo: 15-25KB

## Problemas comunes y soluciones

### "Mi email no llega al destinatario"

Si un email con imágenes adjuntas no llega (rebota o no aparece):
1. Comprueba el tamaño total — si supera 20-25MB probablemente está siendo rechazado
2. Reduce las imágenes a menos de 1MB cada una
3. Si son muchas imágenes, usa Google Drive y envía el enlace

### "Las imágenes en el email se ven borrosas"

Si el destinatario ve las imágenes borrosas:
1. El cliente de email puede estar comprimiendo la imagen al mostrarla inline
2. Solución: adjunta como archivo en lugar de insertar en el cuerpo del email
3. O sube resolución: 1200px de ancho es suficiente para pantallas de escritorio

### "El email va a spam"

Emails con muchas imágenes y poco texto activan filtros:
1. Reduce el número de imágenes o su tamaño
2. Aumenta el contenido de texto
3. Asegúrate de que las imágenes tienen texto alternativo

## Preguntas frecuentes

### ¿Qué tamaño de imagen es el ideal para adjuntar a un email personal?

Para emails personales con fotos, apunta a 1000-1200px de ancho y 150-300KB por imagen. A ese tamaño la foto se ve perfectamente en pantalla, puedes adjuntar 10-15 fotos dentro del límite de 5MB, y la descarga es rápida incluso en conexiones medianas.

### ¿iPhone comprime las fotos al enviarlas por email automáticamente?

Solo si aparece el cuadro de diálogo de reducción al enviar. Si envías directamente desde la app de fotos a Mail, iPhone pregunta si quieres reducir el tamaño. Si adjuntas la foto como archivo desde la app de Mail, se envía en el tamaño original.

### ¿Puedo enviar fotos en formato HEIC por email?

Los iPhones guardan las fotos en HEIC, que Windows y muchos sistemas de correo no abren correctamente. Antes de adjuntar, convierte a JPEG con nuestro [compresor](/) o configura el iPhone para guardar en formato "Compatible" (Ajustes → Cámara → Formatos → "Más compatible").

### ¿Cuánto espacio ocupa un email con fotos en la bandeja del destinatario?

Exactamente el tamaño de los adjuntos más el texto del email. Un email con 5 fotos de 300KB cada una ocupa aproximadamente 1.5MB en la bandeja del destinatario. Si tienen una cuenta con cuota limitada, puede ser un problema acumulado.

### ¿Se puede saber el tamaño de un email antes de enviarlo?

En Gmail web: no directamente desde el editor. En Outlook de escritorio: Archivo → Propiedades → verás el tamaño. Como referencia: suma los tamaños de todos los adjuntos más un ~30KB fijo para el texto y encabezados del email.

## Conclusión

Para reducir imágenes para email, el proceso más eficiente es comprimirlas manualmente antes de adjuntar: redimensiona a 1000-1200px y comprime al 80-85% JPEG. El resultado son archivos de 150-300KB que caben perfectamente en cualquier límite de adjuntos y se ven con buena calidad en cualquier dispositivo.

Los clientes de correo como iPhone y Outlook ofrecen opciones de reducción nativas, pero el control manual siempre produce mejores resultados.

Usa nuestro [compresor de imágenes](/) para el proceso completo antes de adjuntar.
  `
};
